import { z } from 'zod';

/**
 * The paging contract of `uml/18-api-specification.md` §2.6, shared by every
 * list endpoint in the system.
 *
 * It lives in its own module rather than in `schemas/index.ts` because the
 * per-package schema files extend it, and `index.ts` re-exports those - so
 * importing it from there would make a cycle that CommonJS resolves to
 * `undefined` at load time, intermittently and only in the built package.
 */

/**
 * `?page=1&pageSize=20&sort=-createdAt`.
 *
 * **`pageSize` is capped, not refused.** Doc 18 §2.6 says "capped at 100", and
 * a client asking for 5000 means "as many as you will give me" - answering 100
 * is the cooperative reading and it is what the documented word says.
 *
 * The cap is a `transform` and not `.catch(100)`, which would also swallow
 * `pageSize=abc` and answer 100. That is not a request for as many as possible,
 * it is a client defect, and it stays a 422.
 */
export const PaginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce
    .number()
    .int()
    .positive()
    .default(20)
    .transform((size) => Math.min(size, 100)),
  sort: z.string().max(100).optional(),
});
export type PaginationInput = z.infer<typeof PaginationSchema>;

/** The id in a `:id` path segment. Integer, per §2 - every key is a SERIAL. */
export const IdParamSchema = z.object({
  id: z.coerce.number().int().positive(),
});
export type IdParamInput = z.infer<typeof IdParamSchema>;
